import { type ReactNode } from "react";

/**
 * Status badge (design 12.4: "status by icon plus text — never colour alone"; design 12.3:
 * "'Unverifiable' is never green").
 *
 * The badge conveys state THREE ways that are all independent of colour: a glyph, a visible text
 * label, and an accessible name. Colour (from the theme's `--status-*` tokens) is only ever a
 * fourth, redundant channel. The `unknown` level maps to `--status-unknown` (grey), never the
 * healthy green, so an unconfirmed state can never read as "all good".
 *
 * It is screen-agnostic: a screen passes the resolved `level` and an optional `label`/`title`; the
 * badge renders. The levels are the small closed set the screens share (health, connection,
 * verification): healthy / warn / error / unknown, plus `neutral` for a non-status pill.
 */

/** The closed set of status levels. `unknown` is grey (never green); `neutral` is non-status. */
export type StatusLevel = "healthy" | "warn" | "error" | "unknown" | "neutral";

/** The glyph paired with each level so the state is legible without relying on the colour. */
const GLYPH: Readonly<Record<StatusLevel, string>> = {
  healthy: "●",
  warn: "▲",
  error: "✕",
  unknown: "○",
  neutral: "·",
};

/** The default text label per level; a caller may override with its own domain wording. */
const DEFAULT_LABEL: Readonly<Record<StatusLevel, string>> = {
  healthy: "Healthy",
  warn: "Degraded",
  error: "Down",
  unknown: "Unknown",
  neutral: "n/a",
};

export interface StatusBadgeProps {
  /** The status level; `unknown` renders grey (never green). */
  readonly level: StatusLevel;
  /** The visible text; defaults to a per-level word. Shown next to the glyph, never hidden. */
  readonly label?: string | undefined;
  /**
   * A longer accessible name / tooltip (e.g. "Verification: unverifiable — no checkpoint since
   * 10:03"). Defaults to the visible label so the state is always announced as text.
   */
  readonly title?: string;
  /** `md` (default) or `sm` for inline/table use. */
  readonly size?: "sm" | "md";
}

export function StatusBadge(props: StatusBadgeProps): ReactNode {
  const { level } = props;
  const label = props.label ?? DEFAULT_LABEL[level];
  const title = props.title ?? label;
  return (
    <span
      className="status-badge"
      data-level={level}
      data-size={props.size ?? "md"}
      title={title}
    >
      <span className="status-badge__glyph" aria-hidden="true">
        {GLYPH[level]}
      </span>
      {/* The visible label — the state in words, not conveyed by colour or glyph alone. */}
      <span className="status-badge__label">{label}</span>
      {/* The full phrase for assistive tech when `title` adds detail beyond the visible label. */}
      {title !== label && (
        <span className="sr-only" role="status">
          {title}
        </span>
      )}
    </span>
  );
}
